import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import { useProjectContext } from '@/project/ProjectContext';
import { ProjectGate } from '@/features/projects/ProjectGate';
import { Button } from '@/components/ui/Button';
import { LoadingState, StateMessage } from '@/components/StateMessage/StateMessage';
import styles from './ProjectsPage.module.css';

type Graph = {
  nodes: { id: string; kind: string; label: string; meta?: Record<string, unknown> }[];
  edges: { from: string; to: string; kind: string }[];
};

export function OrchestrationGraphPage() {
  const { selectedProject } = useProjectContext();
  const projectId = selectedProject?.id;
  const [exportOpen, setExportOpen] = useState(false);

  const graphQ = useQuery({
    queryKey: ['orchestration-graph', projectId],
    enabled: Boolean(projectId),
    queryFn: () => apiFetch<Graph>(`/api/projects/${projectId}/orchestration-graph`),
  });

  return (
    <ProjectGate>
      <div className={styles.page}>
        {!projectId ? (
          <StateMessage title="Kies een project" />
        ) : graphQ.isLoading ? (
          <LoadingState />
        ) : (
          <>
            <h1 className={styles.title}>Orchestration graph</h1>
            <p className={styles.mutedSmall}>
              Read-only overzicht: sequences, automations (event triggers) en webhooks.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
              <Button type="button" variant="secondary" onClick={() => setExportOpen((v) => !v)}>
                {exportOpen ? 'JSON verbergen' : 'Export JSON'}
              </Button>
              <Link to="/app/automations">→ Automations</Link>
            </div>
            {exportOpen && graphQ.data ? (
              <pre className={styles.mutedSmall} style={{ maxHeight: '14rem', overflow: 'auto' }}>
                {JSON.stringify(graphQ.data, null, 2)}
              </pre>
            ) : null}

            <section className={styles.card}>
              <h2>Nodes ({graphQ.data?.nodes.length ?? 0})</h2>
              <ul className={styles.list}>
                {(graphQ.data?.nodes ?? []).map((n) => (
                  <li key={n.id} className={styles.listItem}>
                    <strong>{n.label}</strong>{' '}
                    <span className={styles.mutedSmall}>
                      · {n.kind}
                      {n.kind === 'automation' && n.meta?.automation_id ? (
                        <>
                          {' '}
                          ·{' '}
                          <Link to="/app/automations">open editor</Link>
                        </>
                      ) : null}
                    </span>
                  </li>
                ))}
              </ul>
            </section>

            <section className={styles.card}>
              <h2>Edges ({graphQ.data?.edges.length ?? 0})</h2>
              <ul className={styles.list}>
                {(graphQ.data?.edges ?? []).slice(0, 80).map((e, i) => (
                  <li key={`${e.from}-${e.to}-${i}`} className={styles.mutedSmall}>
                    {e.from} → {e.to} ({e.kind})
                  </li>
                ))}
              </ul>
            </section>
          </>
        )}
      </div>
    </ProjectGate>
  );
}
